import React, { useState } from 'react'
import {
  Plus,
  Edit2,
  Trash2,
  Check,
  X,
  Loader2,
  AlertTriangle,
  User,
  Shield,
  Mail,
} from 'lucide-react'
import { useAdminUsers, useAdminUserMutations, AdminUser, CreateAdminUserInput } from '@/lib/data/hooks/useAdminUsers'

interface UserFormData {
  email: string
  firstName: string
  lastName: string
  role: 'super_admin' | 'content_manager' | 'support'
  password: string
  confirmPassword: string
}

const initialFormData: UserFormData = {
  email: '',
  firstName: '',
  lastName: '',
  role: 'content_manager',
  password: '',
  confirmPassword: '',
}

export function UserManagement() {
  const { users, isLoading, error: fetchError, refetch } = useAdminUsers()
  const { createUser, updateUser, deleteUser, toggleUserActive, isLoading: isMutating, error: mutationError } = useAdminUserMutations()

  const [showAddModal, setShowAddModal] = useState(false)
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null)
  const [formData, setFormData] = useState<UserFormData>(initialFormData)
  const [formError, setFormError] = useState<string | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)

  const error = fetchError || mutationError || formError

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    setFormError(null)
  }

  const handleSave = async () => {
    // Validation
    if (!formData.email) {
      setFormError('Email is required')
      return
    }
    if (!editingUser && !formData.password) {
      setFormError('Password is required for new users')
      return
    }
    if (!editingUser && formData.password !== formData.confirmPassword) {
      setFormError('Passwords do not match')
      return
    }
    if (!editingUser && formData.password.length < 4) {
      setFormError('Password must be at least 4 characters')
      return
    }

    if (editingUser) {
      const result = await updateUser(editingUser.id, {
        firstName: formData.firstName || undefined,
        lastName: formData.lastName || undefined,
        role: formData.role,
      })
      if (result) {
        handleCloseModal()
        refetch()
      }
    } else {
      const result = await createUser({
        email: formData.email,
        firstName: formData.firstName || undefined,
        lastName: formData.lastName || undefined,
        role: formData.role,
        password: formData.password,
      })
      if (result) {
        handleCloseModal()
        refetch()
      }
    }
  }

  const handleEdit = (user: AdminUser) => {
    setEditingUser(user)
    setFormData({
      email: user.email,
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      role: user.role,
      password: '',
      confirmPassword: '',
    })
    setShowAddModal(true)
  }

  const handleDelete = async (userId: string) => {
    const success = await deleteUser(userId)
    if (success) {
      setDeleteConfirm(null)
      refetch()
    }
  }

  const handleToggleActive = async (user: AdminUser) => {
    await toggleUserActive(user.id, !user.isActive)
    refetch()
  }

  const handleCloseModal = () => {
    setShowAddModal(false)
    setEditingUser(null)
    setFormData(initialFormData)
    setFormError(null)
  }

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'bg-purple-500/10 text-purple-400'
      case 'content_manager':
        return 'bg-blue-500/10 text-blue-400'
      case 'support':
        return 'bg-emerald-500/10 text-emerald-400'
      default:
        return 'bg-[var(--bg-element)] text-[var(--text-muted)]'
    }
  }

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'Super Admin'
      case 'content_manager':
        return 'Content Manager'
      case 'support':
        return 'Support'
      default:
        return role
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-[var(--text-main)]">Team Members</h3>
          <p className="text-[var(--text-muted)] text-sm mt-1">
            Manage admin user accounts and permissions
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-opacity"
        >
          <Plus size={16} />
          Add User
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
          <AlertTriangle size={16} />
          {error}
        </div>
      )}

      {/* Users Table */}
      <div className="bg-[var(--bg-element)] border border-[var(--border)] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-[var(--text-muted)]">
            <User size={48} className="mx-auto mb-4 opacity-50" />
            <p>No users found</p>
            <p className="text-sm mt-1">Add your first team member to get started</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--border)]">
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">
                  User
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">
                  Role
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">
                  Status
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)]">
              {users.map(user => (
                <tr key={user.id} className="hover:bg-[var(--bg-hover)] transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-[var(--primary)] to-purple-500 flex items-center justify-center text-white text-sm font-bold">
                        {user.firstName?.[0] || user.email[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[var(--text-main)]">
                          {user.firstName && user.lastName
                            ? `${user.firstName} ${user.lastName}`
                            : user.email}
                        </p>
                        <p className="text-xs text-[var(--text-muted)]">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${getRoleBadgeColor(user.role)}`}>
                      <Shield size={12} />
                      {getRoleLabel(user.role)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleToggleActive(user)}
                      disabled={isMutating}
                      className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full transition-colors ${
                        user.isActive
                          ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
                          : 'bg-[var(--bg-card)] text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
                      }`}
                    >
                      {user.isActive ? <Check size={12} /> : <X size={12} />}
                      {user.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleEdit(user)}
                        className="p-2 text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-card)] rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 size={16} />
                      </button>
                      {deleteConfirm === user.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleDelete(user.id)}
                            disabled={isMutating}
                            className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                            title="Confirm Delete"
                          >
                            <Check size={16} />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="p-2 text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-card)] rounded-lg transition-colors"
                            title="Cancel"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirm(user.id)}
                          className="p-2 text-[var(--text-muted)] hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[110] p-4">
          <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-[var(--border)]">
              <h2 className="text-xl font-bold text-[var(--text-main)]">
                {editingUser ? 'Edit User' : 'Add New User'}
              </h2>
              <p className="text-[var(--text-muted)] text-sm mt-1">
                {editingUser
                  ? 'Update user information and role'
                  : 'Create a new admin user account'}
              </p>
            </div>

            <div className="p-6 space-y-4">
              {formError && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                  <AlertTriangle size={16} />
                  {formError}
                </div>
              )}

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-[var(--text-main)] mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={!!editingUser}
                    placeholder="user@example.com"
                    className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] pl-10 pr-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)] disabled:opacity-50"
                  />
                </div>
              </div>

              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--text-main)] mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="John"
                    className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-main)] mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Doe"
                    className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
                  />
                </div>
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-[var(--text-main)] mb-2">
                  Role
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
                >
                  <option value="content_manager">Content Manager</option>
                  <option value="support">Support</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>

              {/* Password (only for new users) */}
              {!editingUser && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-main)] mb-2">
                      Password
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Enter password"
                      className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-main)] mb-2">
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="Confirm password"
                      className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
                    />
                  </div>
                </>
              )}
            </div>

            {/* Modal Actions */}
            <div className="p-6 border-t border-[var(--border)] flex items-center justify-end gap-3">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isMutating}
                className="flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isMutating ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check size={16} />
                    {editingUser ? 'Update User' : 'Create User'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
